import {t, Trans} from '@lingui/macro';
import {compact} from 'lodash';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {CheckBox} from '../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {PasswordBox} from '../../../libs/shared/ui/src/components/boxedInputs/passwordBox';
import {TextBox} from '../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {useLocalize} from '../../../srcCommon/internationalization/internationalizationContext';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {Dialog} from '../../components/common/dialogs/dialog';
import {DialogHeader} from '../../components/common/dialogs/dialogHeader';
import {GridAreaItem} from '../../components/common/grid';
import {Paragraph} from '../../components/common/paragraph';
import {TeammateModel} from '../../models/api/teammateModel';
import {putAsync} from '../../services/http/http';
import {SettingsBannersEnum, useSettingsBanners} from '../banners/settingsBannersContext';
import {SettingsFieldLine} from '../common/settingsFieldLine';

/*
 * Props.
 */

interface SettingsProfilePasswordProps {
  user: TeammateModel;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  margin: 20px;
  margin-bottom: 14px;
`;

const StyledContentDiv = styled.div`
  margin-top: 18px;
  display: grid;
  grid-template-areas:
    'current'
    '.'
    'new'
    '.'
    'confirm'
    '.'
    'checkbox';
  grid-template-rows: auto 6px auto 6px auto 7px auto;
`;

const StyledButtonGroupDiv = styled.div`
  padding: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const StyledCheckbox = styled(CheckBox)`
  display: inline-flex;
`;

/*
 * Component.
 */

enum NewPasswordErrorsEnum {
  TOO_SHORT = 'TOO_SHORT',
  DIGIT_AND_UPPERCASE = 'DIGIT_AND_UPPERCASE',
  WEAK = 'WEAK',
}

enum NewPasswordConfirmErrorsEnum {
  DO_NOT_MATCH = 'DO_NOT_MATCH',
}

enum CurrentPasswordErrorsEnum {
  INVALID_PASSWORD = 'INVALID_PASSWORD',
}

export const SettingsProfilePassword: FC<SettingsProfilePasswordProps> = (props) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [currentPasswordErrors, setCurrentPasswordErrors] = useState<CurrentPasswordErrorsEnum[]>([]);

  const [newPassword, setNewPassword] = useState('');
  const [newPasswordErrors, setNewPasswordErrors] = useState<NewPasswordErrorsEnum[]>([]);

  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [newPasswordConfirmErrors, setNewPasswordConfirmErrors] = useState<NewPasswordConfirmErrorsEnum[]>(
    [],
  );

  const {showBanner} = useSettingsBanners();
  const {user} = props;
  const canSave = useMemo(
    () =>
      newPasswordErrors.length === 0 &&
      newPasswordConfirmErrors.length === 0 &&
      [newPassword, currentPassword, newPasswordConfirm].every((p) => p.trim().length > 0),
    [
      newPasswordErrors.length,
      newPasswordConfirmErrors.length,
      newPassword,
      currentPassword,
      newPasswordConfirm,
    ],
  );

  const onCurrentPasswordChange = (newValue: string) => {
    setCurrentPasswordErrors([]);
    setCurrentPassword(newValue);
  };

  const renderCurrentPasswordErrors = () => {
    if (currentPasswordErrors.includes(CurrentPasswordErrorsEnum.INVALID_PASSWORD)) {
      return <Trans id="settingsprofile-incorrectpassword">Incorrect password</Trans>;
    }

    return null;
  };

  const onNewPasswordChange = (newValue: string) => {
    setNewPassword(newValue);

    setNewPasswordErrors(
      compact([
        newValue.trim().length < 8 && NewPasswordErrorsEnum.TOO_SHORT,
        (!/[A-Z]/.test(newValue) || !/\d/.test(newValue)) && NewPasswordErrorsEnum.DIGIT_AND_UPPERCASE,
        isWeakPassword(newValue) && NewPasswordErrorsEnum.WEAK,
      ]),
    );
  };

  const renderNewPasswordErrors = () => {
    if (
      newPasswordErrors.includes(NewPasswordErrorsEnum.DIGIT_AND_UPPERCASE) &&
      !newPasswordErrors.includes(NewPasswordErrorsEnum.TOO_SHORT)
    ) {
      return <Trans id="settingsprofile-includedigituppercase">Include digit and uppercase letter</Trans>;
    }

    if (
      newPasswordErrors.includes(NewPasswordErrorsEnum.TOO_SHORT) &&
      !newPasswordErrors.includes(NewPasswordErrorsEnum.DIGIT_AND_UPPERCASE)
    ) {
      return <Trans id="settingsprofile-mustbe8characters">Must be at least 8 characters</Trans>;
    }

    if (
      newPasswordErrors.includes(NewPasswordErrorsEnum.WEAK) ||
      (newPasswordErrors.includes(NewPasswordErrorsEnum.TOO_SHORT) &&
        newPasswordErrors.includes(NewPasswordErrorsEnum.DIGIT_AND_UPPERCASE))
    ) {
      return (
        <Trans id="settingsprofile-mustbe8anddigituppercase">
          Must be at least 8 characters. Include digit and uppercase letter.
        </Trans>
      );
    }

    return null;
  };

  const onNewPasswordConfirmChange = (newValue: string) => {
    setNewPasswordConfirm(newValue);
    setNewPasswordConfirmErrors(
      compact([newValue !== newPassword && NewPasswordConfirmErrorsEnum.DO_NOT_MATCH]),
    );
  };

  const renderNewPasswordConfirmErrors = () => {
    if (newPasswordConfirmErrors.includes(NewPasswordConfirmErrorsEnum.DO_NOT_MATCH)) {
      return <Trans id="settingsprofile-passwordnotmatching">Password does&apos;t match</Trans>;
    }

    return null;
  };

  const closeDialogAndResetState = () => {
    setIsDialogOpen(false);
    setShowPassword(false);
    setCurrentPassword('');
    setNewPassword('');
    setNewPasswordConfirm('');
    setNewPasswordErrors([]);
  };

  const PasswordField = showPassword ? TextBox : PasswordBox;

  const requestChangePassword = async () => {
    try {
      setNewPasswordErrors([]);
      await putAsync(`${user.url}/password`, {
        old_password: currentPassword,
        new_password: newPassword,
      });

      closeDialogAndResetState();
      showBanner({
        type: SettingsBannersEnum.SUCCESS,
        message: <Trans id="settingsprofile-passwordupdated">Password has been updated</Trans>,
      });
    } catch (error) {
      if (error.status === 400) {
        setNewPasswordErrors([NewPasswordErrorsEnum.WEAK]);
      } else if (error.status === 409) {
        setCurrentPasswordErrors([CurrentPasswordErrorsEnum.INVALID_PASSWORD]);
      } else {
        reportToBugsnag(error);
        showBanner({
          type: SettingsBannersEnum.ERROR,
          message: error.reason || error.message || (
            <Trans id="settingsprofile-genericerror">An error occurred</Trans>
          ),
        });
      }
    }
  };

  const localize = useLocalize();

  const renderDialog = () => {
    if (!isDialogOpen) {
      return null;
    }

    return (
      <Dialog onRequestClose={closeDialogAndResetState}>
        <DialogHeader title={<Trans id="settingsprofile-changepassword">Change password</Trans>} />
        <StyledWrapperDiv>
          <Paragraph>
            <Trans id="settingsprofile-changepassword-copy">
              Please enter your current password to verify your identity and choose a new password.
            </Trans>
          </Paragraph>
          <StyledContentDiv>
            <GridAreaItem area="current">
              <PasswordField
                defaultValue={currentPassword}
                label={<Trans id="settingsprofile-currentpassword">Current password</Trans>}
                onChange={onCurrentPasswordChange}
                hasError={currentPasswordErrors.length > 0}
                errorMessage={renderCurrentPasswordErrors()}
                placeholder={localize(t('settingsprofilepassword-password')`Enter current password`)}
              />
            </GridAreaItem>
            <GridAreaItem area="new">
              <PasswordField
                defaultValue={newPassword}
                label={<Trans id="settingsprofile-newpassword">New password</Trans>}
                onChange={onNewPasswordChange}
                hasError={newPasswordErrors.length > 0}
                errorMessage={renderNewPasswordErrors()}
                message={
                  <Trans id="settingsprofile-mustbe8anddigituppercase">
                    Must be at least 8 characters. Include digit and uppercase letter.
                  </Trans>
                }
                placeholder={localize(t('settingsprofilepassword-newpassword')`Enter new password`)}
              />
            </GridAreaItem>
            <GridAreaItem area="confirm">
              <PasswordField
                defaultValue={newPasswordConfirm}
                label={<Trans id="settingsprofile-confirmnewpassword">Confirm new password</Trans>}
                onChange={onNewPasswordConfirmChange}
                hasError={newPasswordConfirmErrors.length > 0}
                errorMessage={renderNewPasswordConfirmErrors()}
                placeholder={localize(t('settingsprofilepassword-confirmnewpassword')`Re-enter new password`)}
              />
            </GridAreaItem>
            <GridAreaItem area="checkbox">
              <StyledCheckbox
                onChange={() => {
                  setShowPassword((p) => !p);
                }}
                isChecked={showPassword}
              >
                <Trans id="settingsprofile-showpasswords">Show passwords</Trans>
              </StyledCheckbox>
            </GridAreaItem>
          </StyledContentDiv>
        </StyledWrapperDiv>
        <StyledButtonGroupDiv>
          <Button variant={ButtonVariantsEnum.SECONDARY} onClick={closeDialogAndResetState}>
            <Trans id="settingsprofile-cancel">Cancel</Trans>
          </Button>
          <Button variant={ButtonVariantsEnum.PRIMARY} isDisabled={!canSave} onClick={requestChangePassword}>
            <Trans id="settingsprofile-save">Save</Trans>
          </Button>
        </StyledButtonGroupDiv>
      </Dialog>
    );
  };
  return (
    <>
      {renderDialog()}
      <SettingsFieldLine
        title={<Trans id="settingsprofile-password-title">Password</Trans>}
        subtitle={
          <Trans id="settingsprofile-password-subtitle">
            Set a unique password to protect your Front account
          </Trans>
        }
        isSubtitleMultiline
        content={
          <Button
            variant={ButtonVariantsEnum.SECONDARY}
            size={VisualSizesEnum.MEDIUM}
            onClick={() => {
              setIsDialogOpen(true);
            }}
          >
            <Trans id="settingsprofile-changepassword">Change password</Trans>
          </Button>
        }
      />
    </>
  );
};

function isWeakPassword(password: string): boolean {
  if (!password) {
    return true;
  }

  // do not allow leading or trailing whitespaces
  if (password !== password.trim()) {
    return true;
  }

  return false;
}
